import { CommonModule } from '@angular/common';
import {
  Component,
  ElementRef,
  Input,
  OnInit,
  ViewChild,
  forwardRef,
  inject,
} from '@angular/core';
import { FormsModule, NG_VALUE_ACCESSOR } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import {
  NotificationService,
  SeahorseTemplateDirective,
  decodeHtml,
} from '@seahorse/common';
import {
  CustomDataBaseModel,
  FieldDefinitionModel,
  FieldType,
  ObjectDefinitionModel,
  ProxyService,
  NullsPosition,
  ObjectDefinitionHelpers,
} from '@seahorse/domain';
import { of, Subject } from 'rxjs';
import {
  catchError,
  debounceTime,
  finalize,
  map,
  switchMap,
  tap,
} from 'rxjs/operators';
import { LinkedObjectService } from '../linked-object.service';
import { SeahorseFormsModule, BaseControl } from '@seahorse/forms';
import { SeahorseTableComponent } from '../../table/table.component';
import { addNewObjectProcedure } from '../../pages/_index';
import { newActionExcludedObjects } from '../linked-object.model';
import { ColumnModel, TableModel } from '../../table/_index';

type Id = string | number;
type PickerValue = Id | Id[] | null;
type Column = { key: string; name: string };

@Component({
  selector: 'temp-object-picker',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TranslateModule,
    SeahorseFormsModule,
    SeahorseTemplateDirective,
    SeahorseTableComponent,
  ],
  templateUrl: './object-picker.control.html',
  styleUrls: ['./object-picker.control.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => ObjectPickerControl),
      multi: true,
    },
    {
      provide: BaseControl,
      useExisting: forwardRef(() => ObjectPickerControl),
    },
  ],
})
export class ObjectPickerControl
  extends BaseControl<PickerValue>
  implements OnInit
{
  @ViewChild('input', { static: true }) inputRef: ElementRef<HTMLInputElement>;

  @Input() name?: string;
  @Input() multiple = false;
  @Input() fieldDefinition: FieldDefinitionModel;
  @Input() externalObjectDefinition?: ObjectDefinitionModel;
  @Input() isSubmitted?: boolean;
  @Input() override required: boolean;
  @Input() isTable = false;

  suggestions: CustomDataBaseModel[] = [];
  columns: Column[] = [];
  tableModel?: TableModel<any>;

  hideAddButton = false;
  isInitial = true;
  hasConfigurationError = false;

  selectedItems: CustomDataBaseModel[] = [];
  searchModel: any | null = null;

  isLoading = false;
  hasLoadingFailed = false;

  private _linkedObjectData = inject(LinkedObjectService);
  private _notificationService = inject(NotificationService);
  private _proxyServices = inject(ProxyService);
  private _translate = inject(TranslateService);
  private _objectDefinitionHelpers = inject(ObjectDefinitionHelpers);

  private _objectDefinition?: ObjectDefinitionModel;
  private _addNewObjectProcedure = addNewObjectProcedure();
  private search$ = new Subject<string>();

  private _displayFields: string[] = [];
  private _linkedObjectSystemCode?: string;
  private _mappingKey?: string;
  private _tagFilter?: string | null;
  private _foreignKey?: string | null;
  private _searchDisplayFields: string[] = [];
  private _searchSortFields: any[] = [];

  override ngOnInit(): void {
    this.initFromFieldDefinition();
    this.setupSearchStream();
  }

  override writeValue(value: any) {
    super.writeValue(value);
    this.applyIncomingValue(value);
  }

  get autoCompleteItems(): CustomDataBaseModel[] {
    if (!this.isInitial || this.multiple) {
      return this.suggestions ?? [];
    }

    const selected = this.selectedItems?.[0];
    if (!selected) {
      return this.suggestions ?? [];
    }

    const rest = (this.suggestions ?? []).filter(
      (x) => x.__Id !== selected.__Id
    );
    return [selected, ...rest];
  }

  get title() {
    if (!this.isTable) {
      return this.placeholder ?? '';
    }

    const selectText = this._translate.instant('shared.terms.select');
    return `${selectText} ‘${this.placeholder}’`;
  }

  onSearch(query: string) {
    this.search$.next(query);
  }

  onPick(picked: any) {
    if (picked == null) {
      if (!this.multiple) {
        this.clearSelection();
      }
      return;
    }

    if (typeof picked === 'string' || typeof picked === 'number') {
      const raw = `${picked}`.trim();
      if (!raw) {
        if (!this.multiple) {
          this.clearSelection();
        }
        return;
      }

      const idToEmit: any = isNaN(Number(raw)) ? raw : Number(raw);
      const fromSuggestions =
        (this.suggestions ?? []).find((x: any) => this.getId(x) === idToEmit) ??
        null;

      if (fromSuggestions) {
        this.applyPickedObject(fromSuggestions);
        return;
      }

      this.applyPickedId(idToEmit);
      return;
    }

    this.applyPickedObject(picked);
  }

  removeItem(item: CustomDataBaseModel) {
    const id = this.getId(item);
    this.selectedItems = this.selectedItems.filter((x) => this.getId(x) !== id);

    const ids = this.selectedItems.map((x) => this.getId(x));

    if (this.multiple) {
      this.emitOnChange(ids.length ? (ids as any) : null);
    } else {
      const next = (ids[0] ?? null) as any;
      this.searchModel = next;
      this.emitOnChange(next);
    }

    this.cdRef.detectChanges();
  }

  createNewObject() {
    if (!this._objectDefinition) {
      return;
    }

    this.isLoading = true;

    this._addNewObjectProcedure(this._objectDefinition)
      .pipe(finalize(() => (this.isLoading = false)))
      .subscribe((result: any) => {
        const createdId = result?.__Id ?? result?.id;
        if (createdId == null) {
          return;
        }

        const created = result as any;
        created.__Id = createdId;
        created.__DisplayName = created.__DisplayName ?? '';

        const exists = this.selectedItems.some(
          (x) => this.getId(x) === createdId
        );
        if (!exists) {
          this.selectedItems = [...this.selectedItems, created];
        }

        if (this.multiple) {
          const ids = this.selectedItems.map((x) => this.getId(x));
          this.emitOnChange(ids as any);
          this.suggestions = [];
        } else {
          this.searchModel = createdId;
          this.emitOnChange(createdId as any);
        }

        this.cdRef.detectChanges();
      });
  }

  cellValue(row: any, key: string): string {
    if (!row) {
      return '';
    }

    if (key === '__Id') {
      return String(row.__Id ?? row.id ?? '');
    }

    const v = row[key];
    return v == null ? '' : String(v);
  }

  private getFieldLabel(code: string): string {
    const defs =
      this._objectDefinition?.objectFieldDefinitions ??
      this.externalObjectDefinition?.objectFieldDefinitions ??
      [];

    return defs.find((f: any) => f.systemCode === code)?.name ?? code;
  }

  private applyPickedObject(picked: any) {
    if (this.multiple) {
      const pickedId = this.getId(picked);
      const exists = this.selectedItems.some(
        (x: any) => this.getId(x) === pickedId
      );

      if (!exists) {
        this.selectedItems = [...this.selectedItems, picked];
      }

      const ids = this.selectedItems.map((x: any) => this.getId(x));
      this.emitOnChange(ids as any);

      this.suggestions = [];

      this.onTouchedCb?.();
      this.cdRef.detectChanges();
      return;
    }

    const id = this.getId(picked);
    this.selectedItems = [picked];
    this.searchModel = id;
    this.emitOnChange(id as any);

    this.onTouchedCb?.();
    this.cdRef.detectChanges();
  }

  private applyPickedId(idToEmit: any) {
    if (this.multiple) {
      const ids = this.selectedItems.map((x: any) => this.getId(x));
      const next = Array.from(new Set([...ids, idToEmit]));
      this.emitOnChange(next as any);

      this.suggestions = [];

      this.onTouchedCb?.();
      this.cdRef.detectChanges();
      return;
    }

    this.searchModel = idToEmit;
    this.emitOnChange(idToEmit as any);

    this.onTouchedCb?.();
    this.cdRef.detectChanges();
  }

  private clearSelection() {
    this.searchModel = null;
    this.selectedItems = [];
    this.emitOnChange(null);

    this.onTouchedCb?.();
    this.cdRef.detectChanges();
  }

  private setupSearchStream(): void {
    this.search$
      .pipe(
        debounceTime(250),
        tap(() => {
          this.isLoading = true;
          this.hasLoadingFailed = false;
        }),
        switchMap((query) => {
          this.isInitial = false;

          if (!query) {
            this.suggestions = [];
            this.isLoading = false;
            return of([] as CustomDataBaseModel[]);
          }

          const systemCode = this._linkedObjectSystemCode;
          const mappingKey = this._mappingKey;

          if (!systemCode || !mappingKey) {
            this.isLoading = false;
            return of([] as CustomDataBaseModel[]);
          }

          return this._linkedObjectData
            .search(
              systemCode,
              this._displayFields,
              mappingKey,
              query ?? '',
              0,
              25,
              this._tagFilter
            )
            .pipe(
              map((r: any) => (r?.result ?? []) as CustomDataBaseModel[]),
              catchError((e) => {
                this.hasLoadingFailed = true;
                this._notificationService.showError(e, 'shared.terms.failed');
                return of([] as CustomDataBaseModel[]);
              }),
              finalize(() => (this.isLoading = false))
            );
        }),
        tap((items) => {
          this._objectDefinitionHelpers.applyClientSideSort(
            items,
            this._searchSortFields
          );
        })
      )
      .subscribe(
        (items) => (this.suggestions = this.decodeDisplayFields(items))
      );
  }

  private applyIncomingValue(value: PickerValue) {
    const ids = this.asIdArray(value);

    if (!ids.length) {
      this.selectedItems = [];
      if (!this.multiple) {
        this.searchModel = null;
      }
      return;
    }

    this.loadSelectedByIds(ids);
  }

  private loadSelectedByIds(ids: Id[]) {
    const systemCode = this._linkedObjectSystemCode;
    const mappingKey = this._mappingKey;

    if (!systemCode || !mappingKey) {
      this.selectedItems = [];
      return;
    }

    this.isLoading = true;
    this.hasLoadingFailed = false;

    const foreignKey = this._foreignKey ?? null;

    this._linkedObjectData
      .getByKeys(systemCode, mappingKey, foreignKey, ids)
      .pipe(
        catchError((e) => {
          this.hasLoadingFailed = true;
          this._notificationService.showError(e, 'shared.terms.failed');
          return of({ hasResult: false, result: [] as any[] });
        }),
        finalize(() => (this.isLoading = false))
      )
      .subscribe((res: any) => {
        this.selectedItems = this.decodeDisplayFields(
          res?.hasResult ? res.result ?? [] : []
        );
        this._objectDefinitionHelpers.applyClientSideSort(
          this.selectedItems as any[],
          this._searchSortFields
        );

        if (!this.multiple) {
          const first: any = this.selectedItems[0];
          this.searchModel = first?.__Id ?? this.getId(first) ?? null;
        }

        this.cdRef.detectChanges();
      });
  }

  private buildColumnsFromFieldDefinition() {
    let cols: any[] = [];

    if (this._searchDisplayFields?.length) {
      cols = this._searchDisplayFields;
    } else if (this.externalObjectDefinition) {
      cols = (this.externalObjectDefinition as any).displayFields ?? [];
    } else {
      cols =
        (this.fieldDefinition as any)?.collectionConfig?.displayFields ??
        (this.fieldDefinition as any)?.additionalDataModel?.displayFields ??
        [];
    }

    if (!cols.length) {
      this.hasConfigurationError = true;
      return;
    }

    this.columns = cols.map((c: any) => {
      const key = c?.key ?? c;
      const name = c?.name ?? this.getFieldLabel(key);
      return { key, name };
    });

    this.ensureTableModel();
  }

  private ensureTableModel() {
    const fdModel = (this.fieldDefinition as any)
      ?.tableModel as TableModel<any>;

    const cols = (this.columns ?? []).map(
      (c) =>
        ({
          key: c.key,
          name: c.name,
          fieldType: FieldType.SingleLineText,
        } as ColumnModel<any>)
    );

    if (fdModel) {
      this.tableModel = {
        ...fdModel,
        columns: cols,
        disablePaging: true,
        scrollHeight: 'calc(100vh - 350px)',
        sortMode: null,
      };
      return;
    }

    this.tableModel = {
      id: '__Id',
      columns: cols,
      serverSide: false,
      selectionMode: null,
      sortMode: null,
      enableFiltering: false,
      disablePaging: true,
      scrollHeight: 'calc(100vh - 350px)',
      rowActions: [
        {
          key: 'remove',
          label: '',
          icon: 'pi pi-trash',
        } as any,
      ],
    } as TableModel<any>;
  }

  private initFromFieldDefinition() {
    if (!this.fieldDefinition) {
      return;
    }

    const additionalData = FieldDefinitionModel.getAdditionalData(
      this.fieldDefinition
    );

    const sc = (this.fieldDefinition as any)?.fieldRule?.searchConfiguration;
    this._displayFields = sc?.displayFields?.length
      ? sc.displayFields
      : additionalData?.displayFields ?? [];

    this._searchDisplayFields = sc?.displayFields ?? [];
    this._searchSortFields = sc?.sortFields ?? [];

    this._tagFilter = additionalData?.tag ?? null;

    this._linkedObjectSystemCode =
      this.externalObjectDefinition?.systemCode ??
      FieldDefinitionModel.getMappingKeySystemCode(this.fieldDefinition);

    this._foreignKey = this.fieldDefinition
      ? FieldDefinitionModel.getMappingKeyForeignKey(this.fieldDefinition)
      : null;

    const defFromProxy =
      this._proxyServices.objectDefinitions.find(
        (x) => x.systemCode === this._linkedObjectSystemCode
      ) ??
      this._proxyServices.objectDefinitions.find(
        (x) =>
          x.baseObjectDefinition &&
          x.baseObjectDefinition.systemCode === this._linkedObjectSystemCode
      )?.baseObjectDefinition;

    this._mappingKey =
      this.externalObjectDefinition?.mappingKey ??
      defFromProxy?.mappingKey ??
      null;

    const baseFields =
      defFromProxy.baseObjectDefinition?.objectFieldDefinitions ?? [];

    const customFields = defFromProxy.objectFieldDefinitions ?? [];

    const allFields = [
      ...baseFields.filter(
        (bf) => !customFields.some((cf) => cf.systemCode === bf.systemCode)
      ),
      ...customFields,
    ];

    this._objectDefinition = {
      ...defFromProxy,
      objectFieldDefinitions: allFields,
    };

    this.hideAddButton = newActionExcludedObjects.some(
      (key) =>
        this._objectDefinition?.mappingKey &&
        this._objectDefinition.mappingKey.startsWith(key)
    );

    this.buildColumnsFromFieldDefinition();
  }

  private asIdArray(val: PickerValue): Id[] {
    if (val == null) {
      return [];
    }
    return Array.isArray(val) ? val : [val];
  }

  private getId(item: any): Id {
    return item?.__Id ?? item?.id;
  }

  private decodeDisplayFields<T extends CustomDataBaseModel>(items: T[]): T[] {
    return (items ?? []).map((item: any) => ({
      ...item,
      __DisplayName: decodeHtml(item?.__DisplayName),
    }));
  }
}
